import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const RESULT_COLORS = {
  'Home Run': '#e85555',
  'Double': '#d8ceb3',
  'Triple': '#d8ceb3',
  'Single': '#a9c9b1',
};

const AVG_COLOR = (avg) => {
  const v = parseFloat(avg);
  if (isNaN(v)) return 'text-[#658d71]';
  if (v >= 0.300) return 'text-[#a9c9b1]';
  if (v >= 0.250) return 'text-[#d8ceb3]';
  return 'text-[#e85555]';
};

const HitDots = ({ hits, atBats, homeRuns }) => {
  const dots = Array.from({ length: atBats }, (_, i) => {
    if (i < homeRuns) return 'hr';
    if (i < hits) return 'hit';
    return 'out';
  });
  return (
    <div className="flex gap-0.5 flex-wrap max-w-[60px]">
      {dots.map((d, i) => (
        <div
          key={i}
          className="w-2 h-2 rounded-full"
          style={{
            backgroundColor:
              d === 'hr' ? '#e85555' :
              d === 'hit' ? '#a9c9b1' :
              'rgba(101,141,113,0.3)',
          }}
        />
      ))}
    </div>
  );
};

const StatCell = ({ value, highlight, danger, dim }) => (
  <td className={`px-1 py-2 text-center text-[10px] tabular-nums font-bold
    ${highlight ? 'text-[#d8ceb3]' : danger ? 'text-[#e85555]' : dim ? 'text-[#658d71]' : 'text-[#a9c9b1]'}`}>
    {value ?? '–'}
  </td>
);

const BatterDetailRow = ({ player, index, allPlays, onPlayerClick }) => {
  const [expanded, setExpanded] = useState(false);

  const atBatResults = useMemo(() => {
    if (!allPlays?.length) return [];
    return allPlays
      .filter(p => p.matchup?.batter?.id === player.id)
      .map(p => ({
        event: p.result?.event || '--',
        desc: p.result?.description || '',
        inning: p.about?.inning,
        isTop: p.about?.isTopInning,
        rbi: p.result?.rbi || 0,
        pitchCount: p.playEvents?.filter(e => e.isPitch).length || 0,
        exitVelo: p.playEvents?.slice().reverse().find(e => e.hitData?.launchSpeed)?.hitData?.launchSpeed,
        distance: p.playEvents?.slice().reverse().find(e => e.hitData?.totalDistance)?.hitData?.totalDistance,
      }));
  }, [allPlays, player.id]);

  const opsValue = useMemo(() => {
    const obp = player.bb / Math.max(player.ab + player.bb, 1);
    const slg = (player.h + player.hr * 3 + (player.doubles || 0) + (player.triples || 0) * 2) / Math.max(player.ab, 1);
    return (obp + slg).toFixed(3).replace('0.', '.');
  }, [player]);

  const isHot = player.h >= 2 || player.hr > 0;
  const isRBIDay = player.rbi >= 2;

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, x: -4 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.03 }}
        onClick={() => setExpanded(e => !e)}
        className={`border-b border-[rgba(101,141,113,0.15)] cursor-pointer group transition-colors
          ${expanded ? 'bg-[rgba(56,102,70,0.15)]' : 'hover:bg-[rgba(255,255,255,0.03)]'}
          ${isHot ? 'border-l-2 border-l-[#a9c9b1]' : isRBIDay ? 'border-l-2 border-l-[#e85555]' : ''}`}
      >
        <td className="px-2 py-2 text-left">
          <div className="flex items-center gap-2">
            <div className="flex flex-col min-w-0">
              <button
                onClick={e => { e.stopPropagation(); onPlayerClick?.(player.id, player.name); }}
                className="text-[10px] font-black uppercase tracking-wide text-white group-hover:text-[#d8ceb3] transition-colors truncate text-left hover:text-[#d8ceb3]"
              >
                {player.name.split(' ').pop()}
              </button>
              <span className="text-[8px] text-[#658d71] font-bold uppercase">{player.pos}</span>
            </div>
            {(isHot || isRBIDay) && (
              <span className="text-[8px] shrink-0">
                {player.hr > 0 ? '💣' : isHot ? '🔥' : isRBIDay ? '⚡' : ''}
              </span>
            )}
          </div>
        </td>
        <StatCell value={player.ab} dim />
        <StatCell value={player.r || '–'} />
        <StatCell value={player.h} highlight={player.h >= 2} />
        <StatCell value={player.doubles || '–'} highlight={player.doubles > 0} />
        <StatCell value={player.triples || '–'} highlight={player.triples > 0} />
        <StatCell value={player.hr || '–'} danger={player.hr > 0} />
        <StatCell value={player.rbi || '–'} highlight={player.rbi > 0} />
        <StatCell value={player.bb || '–'} />
        <StatCell value={player.k || '–'} danger={player.k >= 3} />
        <StatCell value={player.lob || '–'} dim />
        <td className={`px-1 py-2 text-center text-[10px] tabular-nums font-black ${AVG_COLOR(player.avg)}`}>
          {player.avg || '.000'}
        </td>
        <td className="px-1 py-2 text-center">
          <HitDots hits={player.h} atBats={player.ab} homeRuns={player.hr} />
        </td>
        <td className="px-1 py-2 text-center">
          <span className={`text-[8px] font-bold transition-colors ${expanded ? 'text-[#d8ceb3]' : 'text-[#658d71] group-hover:text-[#a9c9b1]'}`}>
            {expanded ? '▲' : '▼'}
          </span>
        </td>
      </motion.tr>

      <AnimatePresence>
        {expanded && atBatResults.length > 0 && (
          <motion.tr
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <td colSpan={14} className="px-3 pb-3 pt-1 bg-[rgba(0,0,0,0.12)]">
              <div className="flex flex-col gap-1">
                <p className="text-[8px] text-[#658d71] uppercase tracking-widest font-bold mb-1">
                  At-Bat Log · {atBatResults.length} PA
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {atBatResults.map((ab, i) => {
                    const color = RESULT_COLORS[ab.event];
                    const isOut = !color;
                    return (
                      <div
                        key={i}
                        className="flex flex-col border px-2 py-1 bg-[rgba(0,0,0,0.15)] min-w-[80px]"
                        style={{ borderColor: color || 'rgba(101,141,113,0.3)' }}
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-[8px] text-[#658d71] font-bold">
                            {ab.isTop ? '▲' : '▼'}{ab.inning}
                          </span>
                          {ab.rbi > 0 && (
                            <span className="text-[7px] bg-[#e85555] text-white px-1 font-black">
                              {ab.rbi}RBI
                            </span>
                          )}
                        </div>
                        <span
                          className="text-[9px] font-black uppercase leading-tight"
                          style={{ color: color || '#658d71' }}
                        >
                          {ab.event}
                        </span>
                        <div className="flex gap-1 mt-0.5 flex-wrap">
                          {ab.pitchCount > 0 && (
                            <span className="text-[7px] text-[#658d71] font-bold">{ab.pitchCount}P</span>
                          )}
                          {ab.exitVelo && (
                            <span className="text-[7px] text-[#a9c9b1] font-bold">
                              {Math.round(ab.exitVelo)}mph
                            </span>
                          )}
                          {ab.distance && (
                            <span className="text-[7px] text-[#658d71] font-bold">
                              {Math.round(ab.distance)}ft
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
};

const TeamBattingSection = ({ players, abbr, color, allPlays, teamStats, onPlayerClick }) => {
  const totals = teamStats || {};

  return (
    <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)] overflow-x-auto">
      <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-widest" style={{ color }}>
            {abbr} Batting Lines
          </span>
          <span className="text-[8px] text-[#658d71] font-bold uppercase tracking-widest">
            · Click row to expand at-bats · Click name for profile
          </span>
        </div>
        <div className="flex gap-3 text-[9px] font-black uppercase tracking-widest">
          <span className="text-[#a9c9b1]">{totals.hits ?? '–'}H</span>
          <span className="text-[#e85555]">{totals.homeRuns ?? '–'}HR</span>
          <span className="text-[#d8ceb3]">{totals.rbi ?? '–'}RBI</span>
        </div>
      </div>

      <table className="w-full text-center" style={{ minWidth: '560px' }}>
        <thead>
          <tr className="border-b border-[rgba(101,141,113,0.3)] text-[#658d71] text-[8px] uppercase tracking-widest">
            <th className="px-2 py-1.5 text-left w-28">Player</th>
            <th className="px-1 py-1.5 w-7">AB</th>
            <th className="px-1 py-1.5 w-7">R</th>
            <th className="px-1 py-1.5 w-7">H</th>
            <th className="px-1 py-1.5 w-7">2B</th>
            <th className="px-1 py-1.5 w-7">3B</th>
            <th className="px-1 py-1.5 w-7">HR</th>
            <th className="px-1 py-1.5 w-7">RBI</th>
            <th className="px-1 py-1.5 w-7">BB</th>
            <th className="px-1 py-1.5 w-7">K</th>
            <th className="px-1 py-1.5 w-7">LOB</th>
            <th className="px-1 py-1.5 w-12">AVG</th>
            <th className="px-1 py-1.5 w-16">AB Log</th>
            <th className="px-1 py-1.5 w-6"></th>
          </tr>
        </thead>
        <tbody>
          {players.map((p, i) => (
            <BatterDetailRow
              key={p.id || i}
              player={p}
              index={i}
              allPlays={allPlays}
              onPlayerClick={onPlayerClick}
            />
          ))}
          {/* Totals Row */}
          <tr className="border-t-2 border-[#658d71] bg-[rgba(0,0,0,0.15)]">
            <td className="px-2 py-1.5 text-left text-[8px] font-black uppercase tracking-widest text-[#658d71]">
              TOTALS
            </td>
            {[
              totals.atBats,
              totals.runs,
              totals.hits,
              totals.doubles,
              totals.triples,
              totals.homeRuns,
              totals.rbi,
              totals.baseOnBalls,
              totals.strikeOuts,
              totals.leftOnBase,
            ].map((v, i) => (
              <td key={i} className="px-1 py-1.5 text-[9px] tabular-nums font-black text-white text-center">
                {v ?? '–'}
              </td>
            ))}
            <td className="px-1 py-1.5 text-[9px] tabular-nums font-black text-[#d8ceb3] text-center">
              {totals.atBats > 0
                ? (totals.hits / totals.atBats).toFixed(3).replace('0.', '.')
                : '--'}
            </td>
            <td colSpan={2} />
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const DetailedBattingLines = ({ boxData, allPlays, awayAbbr, homeAbbr, onPlayerClick }) => {
  const [activeTeam, setActiveTeam] = useState('both');

  const enrichedPlayers = useMemo(() => {
    const enrich = (players, side) => {
      return players.map(p => {
        // Count doubles/triples from allPlays
        const playerPlays = allPlays?.filter(play => play.matchup?.batter?.id === p.id) || [];
        const doubles = playerPlays.filter(play => play.result?.event === 'Double').length;
        const triples = playerPlays.filter(play => play.result?.event === 'Triple').length;
        return { ...p, doubles, triples };
      });
    };
    return {
      away: enrich(boxData?.away || [], 'away'),
      home: enrich(boxData?.home || [], 'home'),
    };
  }, [boxData, allPlays]);

  const showAway = activeTeam === 'both' || activeTeam === 'away';
  const showHome = activeTeam === 'both' || activeTeam === 'home';

  return (
    <div className="flex flex-col gap-4">
      {/* Legend + Filter */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1.5">
          {[['both', 'Both Teams'], ['away', awayAbbr], ['home', homeAbbr]].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setActiveTeam(val)}
              className={`text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 border transition-colors
                ${activeTeam === val
                  ? 'bg-[#658d71] border-[#a9c9b1] text-white'
                  : 'border-[#658d71] text-[#a9c9b1] hover:text-white'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-[8px] uppercase tracking-widest font-bold">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-[#a9c9b1]" />
            <span className="text-[#a9c9b1]">Hit</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-[#e85555]" />
            <span className="text-[#e85555]">HR</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-[rgba(101,141,113,0.3)]" />
            <span className="text-[#658d71]">Out</span>
          </div>
          <span className="text-[#658d71]">· 🔥 Multi-hit · 💣 HR</span>
        </div>
      </div>

      {showAway && enrichedPlayers.away.length > 0 && (
        <TeamBattingSection
          players={enrichedPlayers.away}
          abbr={awayAbbr}
          color="#a9c9b1"
          allPlays={allPlays}
          teamStats={boxData?.awayTeamStats}
          onPlayerClick={onPlayerClick}
        />
      )}

      {showHome && enrichedPlayers.home.length > 0 && (
        <TeamBattingSection
          players={enrichedPlayers.home}
          abbr={homeAbbr}
          color="#e85555"
          allPlays={allPlays}
          teamStats={boxData?.homeTeamStats}
          onPlayerClick={onPlayerClick}
        />
      )}

      {!enrichedPlayers.away.length && !enrichedPlayers.home.length && (
        <div className="text-[#658d71] text-xs text-center py-8 uppercase tracking-widest">
          No batting data available
        </div>
      )}
    </div>
  );
};

export default DetailedBattingLines;